import { ActionIcon, AppShell, Avatar, Burger, Group, Image, Menu, NavLink, ScrollArea, Text, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconHelp, IconLogout, IconPuzzle, IconShieldCog, IconUser } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { NavLink as RouterNavLink, Outlet, useNavigate } from 'react-router';
import { useLogout } from '@/api/session';
import { Logo } from '@/components/Logo';
import { WorkspaceSwitcher } from '@/features/workspace/WorkspaceSwitcher';
import { colorFor, initials } from '@/lib/format';
import { OfflineIndicator } from '@/offline/OfflineIndicator';
import { LocationIndicator } from './LocationIndicator';
import { NotificationBell } from './NotificationBell';
import { SessionBanners } from './SessionBanners';
import { isNavItemVisible, navigation } from './navigation';
import { useCurrentWorkspace } from './WorkspaceContext';

/** App-Grundgerüst: Kopfzeile (UI-01) und Seitennavigation (UI-02/04). */
export function AppLayout() {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const navigate = useNavigate();
  const logout = useLogout();
  const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] = useDisclosure();

  return (
    <AppShell header={{ height: 56 }} navbar={{ width: 240, breakpoint: 'sm', collapsed: { mobile: !mobileOpened } }} padding="md">
      <AppShell.Header style={ctx.workspace.brandColor ? { borderTop: `3px solid ${ctx.workspace.brandColor}` } : undefined}>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="md" wrap="nowrap">
            <Burger opened={mobileOpened} onClick={toggleMobile} hiddenFrom="sm" size="sm" aria-label={t('nav.toggle')} />
            <Logo />
            {ctx.workspace.logoUrl && <Image src={ctx.workspace.logoUrl} h={28} w="auto" fit="contain" alt={ctx.workspace.name} visibleFrom="sm" />}
            <WorkspaceSwitcher />
          </Group>
          <Group gap="xs" wrap="nowrap">
            <Tooltip label={t('header.integrations')}>
              <ActionIcon variant="subtle" color="gray" size="lg" aria-label={t('header.integrations')} onClick={() => navigate('/settings/integrations')}>
                <IconPuzzle size={20} />
              </ActionIcon>
            </Tooltip>
            <LocationIndicator />
            <OfflineIndicator />
            <NotificationBell />
            <Tooltip label={t('header.help')}>
              <ActionIcon variant="subtle" color="gray" size="lg" aria-label={t('header.help')} component="a" href="/swagger-ui.html" target="_blank">
                <IconHelp size={20} />
              </ActionIcon>
            </Tooltip>
            <Menu position="bottom-end" width={220} withinPortal>
              <Menu.Target>
                <ActionIcon variant="transparent" size="lg" aria-label={t('header.profile')}>
                  <Avatar size={32} radius="xl" color={colorFor(ctx.user.id)}>
                    {initials(ctx.user.name)}
                  </Avatar>
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>
                  <Text size="sm" fw={500} c="var(--mantine-color-text)">
                    {ctx.user.name}
                  </Text>
                  <Text size="xs">{ctx.user.email}</Text>
                </Menu.Label>
                <Menu.Item leftSection={<IconUser size={16} />} onClick={() => navigate('/profile')}>
                  {t('header.profile')}
                </Menu.Item>
                {ctx.user.superadmin && (
                  <Menu.Item leftSection={<IconShieldCog size={16} />} onClick={() => navigate('/admin')}>
                    {t('header.admin')}
                  </Menu.Item>
                )}
                <Menu.Divider />
                <Menu.Item
                  leftSection={<IconLogout size={16} />}
                  onClick={() => logout.mutate(undefined, { onSettled: () => navigate('/login') })}
                >
                  {t('header.logout')}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar>
        <ScrollArea p="xs">
          <nav aria-label={t('nav.label')}>
            {navigation.map((group) => {
              const items = group.items.filter((item) => isNavItemVisible(item, ctx));
              if (items.length === 0) return null;
              return (
                <div key={group.key}>
                  <Text size="xs" fw={600} c="dimmed" tt="uppercase" px="sm" pt="md" pb={4}>
                    {t(`nav.groups.${group.key}`)}
                  </Text>
                  {items.map((item) => (
                    <NavLink
                      key={item.key}
                      component={RouterNavLink}
                      to={item.path}
                      label={t(`nav.items.${item.key}`)}
                      leftSection={<item.icon size={18} stroke={1.6} />}
                      onClick={closeMobile}
                    />
                  ))}
                </div>
              );
            })}
          </nav>
        </ScrollArea>
      </AppShell.Navbar>

      <AppShell.Main>
        <SessionBanners />
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
